import type { KeyboardEvent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { PixelPlaceholderLoader, SimpleChipItem, Textfield } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import {
    INPUT_HEIGHT,
    InlineErrorMessage,
    LLMDomainAutocomplete,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMDomainAutocomplete";

import type { IRawCompetitor } from "../../types";

import { StyledCompetitorDeleteButton } from "./CompetitorsSection.styled";

const MAX_BRANDS_LINES = 2;

interface ICompetitorFieldProps {
    competitor: IRawCompetitor;
    isRemovable: boolean;
    isLoading: boolean;
    isReadOnly?: boolean;
    autoFocus?: boolean;
    domainError?: string;
    brandError?: string;
    onChange: (competitor: IRawCompetitor) => void;
    onRemove: () => void;
}

export const CompetitorField = ({
    competitor,
    isRemovable,
    isLoading,
    isReadOnly,
    autoFocus,
    domainError,
    brandError,
    onChange,
    onRemove,
}: ICompetitorFieldProps) => {
    const [brandDraft, setBrandDraft] = useState<string>("");
    const brandsContainerRef = useRef<HTMLDivElement>(null);
    const brandFieldRef = useRef<Textfield>(null);
    const trimmedBrand = brandDraft.trim();
    const isDuplicate = useMemo<boolean>(() => {
        const lowercaseTrimmed = trimmedBrand.toLowerCase();
        return !!lowercaseTrimmed && competitor.brands.some((brand) => brand.toLowerCase() === lowercaseTrimmed);
    }, [trimmedBrand, competitor.brands]);

    const isBrandValid = !!trimmedBrand && !isDuplicate;
    const duplicateError = isDuplicate ? translate("llmTracker.campaignQuery.editBrand.error.duplicate") : "";

    const addBrand = (): void => {
        if (!isBrandValid) {
            return;
        }

        onChange({
            ...competitor,
            brands: [...competitor.brands, trimmedBrand],
        });
        setBrandDraft("");
        brandFieldRef.current?.clearValue?.();
    };

    const onBrandKeyDown = (ev: KeyboardEvent<HTMLInputElement>): void => {
        if (ev.key === "Enter") {
            ev.preventDefault();
            addBrand();
        }
    };

    const removeBrand = (brand: string): void => {
        onChange({
            ...competitor,
            brands: competitor.brands.filter((b) => b !== brand),
        });
    };

    useEffect(() => {
        if (brandsContainerRef.current) {
            brandsContainerRef.current.scrollTo({ top: brandsContainerRef.current.scrollHeight, behavior: "smooth" });
        }
    }, [competitor.brands]);

    const brandFieldError = duplicateError || brandError || "";

    return (
        <Flex
            flexDirection="column"
            gap="8px"
        >
            {isLoading ? (
                <PixelPlaceholderLoader
                    width="100%"
                    height="60px"
                />
            ) : (
                <Flex
                    alignItems="flex-start"
                    gap="12px"
                    width="100%"
                    minWidth="0"
                >
                    <Flex
                        data-automation="llm-competitor-domain-input-container"
                        flex="1 1 0"
                        minWidth="0"
                        position="relative"
                        flexDirection="column"
                    >
                        <LLMDomainAutocomplete
                            selectedDomain={competitor.domain}
                            setSelectedDomain={(domain) => (domain ? onChange({ ...competitor, domain }) : undefined)}
                            onCloseClick={isReadOnly ? undefined : () => onChange({ ...competitor, domain: null })}
                            placeholder={translate("llm.competitors_section.field.placeholder")}
                            searchIcon={!competitor.domain ? "search" : undefined}
                            autoFocus={autoFocus}
                            displaySelectedDomainIcon
                            usePortalForDropdown
                            isDisabled={isReadOnly}
                            width="100%"
                            height={INPUT_HEIGHT}
                            error={!!domainError}
                        />
                        {domainError ? (
                            <InlineErrorMessage
                                fontVariant="caption"
                                color={colorsPalettes.red[400]}
                                data-automation="llm-competitor-domain-error"
                            >
                                {domainError}
                            </InlineErrorMessage>
                        ) : null}
                    </Flex>
                    {!isReadOnly ? (
                        <Flex
                            data-automation="llm-competitor-brand-input-container"
                            flex="1 1 0"
                            minWidth="0"
                            position="relative"
                            flexDirection="column"
                        >
                            <Textfield
                                ref={brandFieldRef}
                                data-automation="llm-competitor-brand-input"
                                placeholder={translate("llm.setup.modal.competitor.brand.placeholder")}
                                defaultValue={brandDraft}
                                onChange={setBrandDraft}
                                onKeyDown={onBrandKeyDown}
                                error={!!brandFieldError}
                                ApplyButtonText={translate("global.add")}
                                onApplyClick={isBrandValid ? addBrand : undefined}
                            />
                            {brandFieldError ? (
                                <InlineErrorMessage
                                    fontVariant="caption"
                                    color={colorsPalettes.red[400]}
                                    data-automation="llm-competitor-brand-error"
                                >
                                    {brandFieldError}
                                </InlineErrorMessage>
                            ) : null}
                        </Flex>
                    ) : null}
                    {isRemovable ? (
                        <Flex
                            flex="0 0 32px"
                            width="32px"
                            height={INPUT_HEIGHT}
                            alignItems="center"
                            justifyContent="center"
                        >
                            <StyledCompetitorDeleteButton
                                type="flat"
                                iconName="delete"
                                iconSize="sm"
                                onClick={onRemove}
                                data-automation="llm-competitor-delete"
                            />
                        </Flex>
                    ) : null}
                </Flex>
            )}
            {!isLoading && competitor.brands.length ? (
                <Flex
                    ref={brandsContainerRef}
                    flexWrap="wrap"
                    overflow="auto"
                    maxHeight={`${40 * MAX_BRANDS_LINES}px`}
                    gap="4px"
                >
                    {competitor.brands.map((brand) => (
                        <SimpleChipItem
                            key={brand}
                            text={brand}
                            hideActionButton={isReadOnly}
                            onCloseItem={() => removeBrand(brand)}
                        />
                    ))}
                </Flex>
            ) : null}
        </Flex>
    );
};
